import { Track } from "@/src/lib/tracking";
import { useState } from "react";
import { useParams } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";

import {
  finalizeMemberPlaidAuthorization,
  upsertPlaidConnectionInList,
  type PlaidMemberAuthorizationOutcome,
} from "./memberPlaidApi";
import { resolvePlaidClientType } from "./plaidPlatform";
import { PLAID_CONNECTIONS_QUERY_KEY } from "./usePlaidConnections";
import { PORTFOLIO_CONNECTIONS_QUERY_KEY } from "./usePortfolioConnections";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";
import { useMemoizedFn } from "ahooks";
import type { FinancialConnection } from "@features/api/memberApi";

export interface PlaidLinkExitError {
  error_type: string;
  error_code: string;
  error_message: string;
  display_message: string;
  institution_name: string;
}

export interface UseExchangeAccessTokenResult {
  connectionId: string | null;
  authorized: boolean;
  loading: boolean;
  error: string | null;
  finalizePlaidAuthorization: (
    linkToken: string,
  ) => Promise<PlaidMemberAuthorizationOutcome | null>;
  reportLinkExitError: (error: PlaidLinkExitError) => Promise<void>;
  reset: () => void;
}

export const useExchangeAccessToken = (): UseExchangeAccessTokenResult => {
  const { device } = useParams<{ device?: DeviceType }>();
  const { memberApi } = useOspApiClientProvider();
  const queryClient = useQueryClient();
  const clientType = resolvePlaidClientType(device);

  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [authorized, setAuthorized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authorizationMutation = useMutation({
    mutationFn: async (linkToken: string) => {
      if (!memberApi) {
        throw new Error("memberApi is not available");
      }
      return finalizeMemberPlaidAuthorization(memberApi, clientType, linkToken);
    },
  });

  const finalizePlaidAuthorization = useMemoizedFn(
    async (
      linkToken: string,
    ): Promise<PlaidMemberAuthorizationOutcome | null> => {
      setError(null);
      try {
        const outcome = await authorizationMutation.mutateAsync(linkToken);

        if (outcome.kind === "institution_already_linked") {
          setConnectionId(null);
          setAuthorized(false);
          return outcome;
        }

        queryClient.setQueryData<FinancialConnection[]>(
          PLAID_CONNECTIONS_QUERY_KEY,
          (prev) => upsertPlaidConnectionInList(prev ?? [], outcome.connection),
        );
        await queryClient.invalidateQueries({
          queryKey: PORTFOLIO_CONNECTIONS_QUERY_KEY,
        });
        setConnectionId(outcome.connectionId);
        setAuthorized(true);
        return outcome;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Authorization request failed";
        setError(message);
        setConnectionId(null);
        setAuthorized(false);
        return null;
      }
    },
  );

  const reportLinkExitError = useMemoizedFn(
    async (linkExitError: PlaidLinkExitError): Promise<void> => {
      Track.log("info", "useExchangeAccessToken", "link exit", {
        cause: String(linkExitError),
      });
    },
  );

  const reset = useMemoizedFn(() => {
    setConnectionId(null);
    setAuthorized(false);
    setError(null);
  });

  return {
    connectionId,
    authorized,
    loading: authorizationMutation.isPending,
    error,
    finalizePlaidAuthorization,
    reportLinkExitError,
    reset,
  };
};
